import {
  ICitaionRuleEscalationTierConfiguration,
  ICitationConfiguration,
  ICitationConfigurationUpdate,
  ICitationRuleConfiguration,
} from 'common/services/configuration-service/configuration.service.types';
import {
  ICitationConfigurationFormValues,
  ICitationEscalationTierFormValue,
  ICitationRuleFormValue,
} from './citation-configuration-form.types';

export const isFilled = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

export const toNullableNumber = (value: number | string | null | undefined): number | null => {
  if (!isFilled(value)) {
    return null;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

export const createEmptyEscalationTier = (order = 0): ICitationEscalationTierFormValue => ({
  order,
  speedFrom: null,
  speedTo: null,
  isWarningOnly: false,
  residentFineAmount: null,
  residentSuspendTransponderInDays: null,
  visitorFineAmount: null,
  visitorSuspendTransponderInDays: null,
  revokeDrivingPrivilegesInDays: null,
  isResidentFineAmountEnabled: false,
  isResidentSuspendTransponderInDaysEnabled: false,
  isVisitorFineAmountEnabled: false,
  isVisitorSuspendTransponderInDaysEnabled: false,
  isRevokeDrivingPrivilegesInDaysEnabled: false,
});

export const createEmptyRule = (order = 0): ICitationRuleFormValue => ({
  order,
  legalNotes: '',
  escalationTiers: [createEmptyEscalationTier()],
});

const mapTierToFormValue = (
  tier: ICitaionRuleEscalationTierConfiguration,
  order: number,
  includeSpeedRange: boolean,
): ICitationEscalationTierFormValue => ({
  ...tier,
  order,
  speedFrom: includeSpeedRange ? tier.speedFrom ?? null : null,
  speedTo: includeSpeedRange ? tier.speedTo ?? null : null,
  isWarningOnly: tier.isWarningOnly ?? false,
  residentFineAmount: tier.residentFineAmount ?? null,
  residentSuspendTransponderInDays: tier.residentSuspendTransponderInDays ?? null,
  visitorFineAmount: tier.visitorFineAmount ?? null,
  visitorSuspendTransponderInDays: tier.visitorSuspendTransponderInDays ?? null,
  revokeDrivingPrivilegesInDays: tier.revokeDrivingPrivilegesInDays ?? null,
  isResidentFineAmountEnabled: !tier.isWarningOnly && isFilled(tier.residentFineAmount),
  isResidentSuspendTransponderInDaysEnabled: !tier.isWarningOnly && isFilled(tier.residentSuspendTransponderInDays),
  isVisitorFineAmountEnabled: !tier.isWarningOnly && isFilled(tier.visitorFineAmount),
  isVisitorSuspendTransponderInDaysEnabled: !tier.isWarningOnly && isFilled(tier.visitorSuspendTransponderInDays),
  isRevokeDrivingPrivilegesInDaysEnabled: !tier.isWarningOnly && isFilled(tier.revokeDrivingPrivilegesInDays),
});

export const getRuleInitData = (
  rules: ICitationRuleConfiguration[] | null | undefined,
  isEscalationTiersEnabled: boolean,
): ICitationRuleFormValue[] =>
  (rules ?? []).map((rule, ruleIndex) => {
    const sourceTiers = rule.escalationTiers ?? [];
    const visibleTiers = isEscalationTiersEnabled ? sourceTiers : sourceTiers.slice(0, 1);

    return {
      order: ruleIndex,
      legalNotes: rule.legalNotes ?? '',
      escalationTiers:
        visibleTiers.length > 0
          ? visibleTiers.map((tier, tierIndex) => mapTierToFormValue(tier, tierIndex, isEscalationTiersEnabled))
          : [createEmptyEscalationTier()],
    };
  });

export const mapConfigurationToFormValues = (
  configuration: ICitationConfiguration,
  isEscalationTiersEnabled: boolean,
): ICitationConfigurationFormValues => ({
  ...configuration,
  firstCitationWarningToleranceInMPH: configuration.firstCitationWarningToleranceInMPH ?? null,
  firstCitationEscalationToleranseInMPH: configuration.firstCitationEscalationToleranseInMPH ?? null,
  isFirstCitationWarningToleranceInMPHEnabled: isFilled(configuration.firstCitationWarningToleranceInMPH),
  isFirstCitationEscalationToleranseInMPHEnabled: isFilled(configuration.firstCitationEscalationToleranseInMPH),
  rules: getRuleInitData(configuration.rules, isEscalationTiersEnabled),
});

const getEnabledNumber = (enabled: boolean, value: number | string | null | undefined): number | null =>
  enabled ? toNullableNumber(value) : null;

const mapEscalationTierToModel = (
  tier: ICitationEscalationTierFormValue,
  order: number,
  includeSpeedRange: boolean,
): ICitaionRuleEscalationTierConfiguration => {
  const isWarningOnly = Boolean(tier.isWarningOnly);

  return {
    order,
    speedFrom: includeSpeedRange ? toNullableNumber(tier.speedFrom) : null,
    speedTo: includeSpeedRange ? toNullableNumber(tier.speedTo) : null,
    isWarningOnly,
    residentFineAmount: isWarningOnly ? null : getEnabledNumber(tier.isResidentFineAmountEnabled, tier.residentFineAmount),
    residentSuspendTransponderInDays: isWarningOnly
      ? null
      : getEnabledNumber(tier.isResidentSuspendTransponderInDaysEnabled, tier.residentSuspendTransponderInDays),
    visitorFineAmount: isWarningOnly ? null : getEnabledNumber(tier.isVisitorFineAmountEnabled, tier.visitorFineAmount),
    visitorSuspendTransponderInDays: isWarningOnly
      ? null
      : getEnabledNumber(tier.isVisitorSuspendTransponderInDaysEnabled, tier.visitorSuspendTransponderInDays),
    revokeDrivingPrivilegesInDays: isWarningOnly
      ? null
      : getEnabledNumber(tier.isRevokeDrivingPrivilegesInDaysEnabled, tier.revokeDrivingPrivilegesInDays),
  };
};

export const mapFormValuesToConfigurationUpdate = (
  values: ICitationConfigurationFormValues,
  isEscalationTiersEnabled: boolean,
): ICitationConfigurationUpdate => {
  const rules: ICitationRuleConfiguration[] = (values.rules ?? []).map((rule, ruleIndex) => {
    const sourceTiers = rule.escalationTiers ?? [];
    const tiersToSave = isEscalationTiersEnabled ? sourceTiers : sourceTiers.slice(0, 1);

    return {
      order: ruleIndex,
      legalNotes: rule.legalNotes?.trim() || undefined,
      escalationTiers:
        tiersToSave.length > 0
          ? tiersToSave.map((tier, tierIndex) => mapEscalationTierToModel(tier, tierIndex, isEscalationTiersEnabled))
          : [mapEscalationTierToModel(createEmptyEscalationTier(), 0, isEscalationTiersEnabled)],
    };
  });

  return {
    citations: {
      citationPaymentCommissionInPercentages: toNullableNumber(values.citationPaymentCommissionInPercentages),
      canceledCitationRemoveDelayInDays: toNullableNumber(values.canceledCitationRemoveDelayInDays),
      generalInformation: values.generalInformation,
      unassignedCitationRemoveDelayInDays: toNullableNumber(values.unassignedCitationRemoveDelayInDays),
      citationResetInDays: toNullableNumber(values.citationResetInDays),
      delinquentCitationInDays: values.isAutoIssueInvoice ? toNullableNumber(values.delinquentCitationInDays) : null,
      isAutoSuspendTransponders: values.isAutoSuspendTransponders,
      isAutoIssueInvoice: values.isAutoIssueInvoice,
      isPreventVendorCitations: values.isPreventVendorCitations,
      firstCitationWarningToleranceInMPH: {
        value: values.isFirstCitationWarningToleranceInMPHEnabled ? toNullableNumber(values.firstCitationWarningToleranceInMPH) : null,
      },
      firstCitationEscalationToleranseInMPH: {
        value: values.isFirstCitationEscalationToleranseInMPHEnabled
          ? toNullableNumber(values.firstCitationEscalationToleranseInMPH)
          : null,
      },
      rules,
    },
  };
};
